import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, Info, ChevronRight } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { PropertyPanel } from '../ui/PropertyPanel';

interface DeviceSwapModalProps {
  device: any;
  datacenterCode: string;
  onClose: () => void;
}

export const DeviceSwapModal: React.FC<DeviceSwapModalProps> = ({
  device,
  datacenterCode,
  onClose
}) => {
  const navigate = useNavigate();
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  
  const [generating, setGenerating] = useState(false);

  // Fetch catalog on mount
  useEffect(() => {
    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const res = await api.get('/api/equipment/catalog', {
          params: { limit: 100 }
        });
        setCatalogItems(res.data || []);
      } catch (err) {
        console.error('Failed to load catalog:', err);
        toast.error('Не удалось загрузить каталог оборудования');
      } finally {
        setLoadingCatalog(false);
      }
    };
    fetchCatalog();
  }, []);

  // Filter catalog items by search query
  const filteredCatalog = catalogItems.filter(item => {
    const name = `${item.manufacturer} ${item.model}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  // Run Impact Analysis when selected catalog model changes
  const runImpactAnalysis = async (catalogId: string) => {
    if (!catalogId) {
      setAnalysisResult(null);
      setAnalysisError(null);
      return;
    }
    setAnalyzing(true);
    setAnalysisResult(null);
    setAnalysisError(null);
    try {
      const res = await api.post(`/api/datacenters/${datacenterCode}/impact-analysis/equipment-swap`, {
        old_equipment_id: device.id,
        new_equipment_catalog_id: Number(catalogId)
      });
      setAnalysisResult(res.data);
    } catch (err: any) {
      const errMsg = err.response?.data?.detail || 'Ошибка при проведении анализа влияния';
      setAnalysisError(errMsg);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCatalogChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setSelectedCatalogId(id);
    runImpactAnalysis(id);
  };

  // Generate Work Order
  const handleGenerateWorkOrder = async () => {
    if (!selectedCatalogId) return;
    setGenerating(true);
    try {
      await api.post(`/api/datacenters/${datacenterCode}/impact-analysis/equipment-swap/generate-work-order`, {
        old_equipment_id: device.id,
        new_equipment_catalog_id: Number(selectedCatalogId)
      });
      toast.success('Наряд на замену оборудования успешно создан!');
      onClose();
      navigate(`/${datacenterCode}/tasks`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Не удалось сгенерировать наряд');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <PropertyPanel title="Замена оборудования" isOpen={true} onClose={onClose}>
      <div className="flex flex-col h-full space-y-5 text-xs font-mono pb-8">
        {/* Info banner */}
        <div className="p-3 bg-cyan-950/30 border border-cyan-800/40 rounded-lg text-cyan-300 flex items-start gap-2.5 shrink-0">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Сценарий замены проанализирует коммутацию прибора <span className="font-bold text-slate-100">{device.name}</span>, проверит габаритные пересечения в стойке и создаст пошаговую задачу для дежурного инженера.
          </div>
        </div>

        {/* Model selection */}
        <div className="space-y-2 shrink-0">
          <label className="block text-slate-400 font-bold uppercase tracking-wider text-[10px]">Выберите модель на замену:</label>
          <div className="flex flex-col gap-2">
            <input
              type="text"
              placeholder="Фильтр по названию..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-slate-200 focus:outline-none focus:border-cyan-500 text-xs"
            />
            <select
              value={selectedCatalogId}
              onChange={handleCatalogChange}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-slate-200 focus:outline-none focus:border-cyan-500 text-xs"
              disabled={loadingCatalog}
            >
              <option value="">-- Выберите модель --</option>
              {filteredCatalog.map(item => (
                <option key={item.id} value={item.id}>
                  {item.manufacturer} {item.model} ({item.u_height}U, {item.power_w}W)
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Action Button: Analyze */}
        {!analysisResult && (
          <div className="flex justify-end pt-2 shrink-0">
            <button
              onClick={() => runImpactAnalysis(selectedCatalogId)}
              disabled={!selectedCatalogId || analyzing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition disabled:opacity-50"
            >
              {analyzing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {analyzing ? 'Анализ...' : 'Проанализировать'}
            </button>
          </div>
        )}


          {/* Analysis status / Loader */}
          {analyzing && (
            <div className="flex items-center justify-center gap-2 p-8 text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin text-cyan-400" />
              <span>Расчет коллизий и коммутации...</span>
            </div>
          )}

          {/* Overlap Error Warning */}
          {analysisError && (
            <div className="p-4 bg-red-950/40 border border-red-800/60 rounded-lg text-red-300 flex items-start gap-2.5">
              <AlertTriangle className="w-5 h-5 shrink-0 text-red-400" />
              <div>
                <span className="font-bold block text-slate-100">Ошибка физического пересечения:</span>
                {analysisError}
              </div>
            </div>
          )}

          {/* Analysis Results Display */}
          {analysisResult && (
            <div className="space-y-4 animate-in fade-in duration-200">
              {/* Success validation */}
              <div className="p-3 bg-emerald-950/30 border border-emerald-800/40 rounded-lg text-emerald-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span>Физические ограничения проверены: коллизий нет, размеры корректны.</span>
              </div>

              {/* Stats overview */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-slate-950/40 border border-slate-800 rounded-lg">
                  <span className="text-[10px] text-slate-500 uppercase block mb-1">Всего кабелей</span>
                  <span className="text-base font-bold text-slate-200">{analysisResult.total_cables_affected}</span>
                </div>
                <div className="p-3 bg-slate-950/40 border border-slate-800 rounded-lg">
                  <span className="text-[10px] text-slate-500 uppercase block mb-1">Будет переподключено</span>
                  <span className="text-base font-bold text-cyan-400">{analysisResult.cables_to_repatch.length}</span>
                </div>
                <div className="p-3 bg-slate-950/40 border border-slate-800 rounded-lg">
                  <span className="text-[10px] text-slate-500 uppercase block mb-1">Осиротевшие порты</span>
                  <span className="text-base font-bold text-amber-500">{analysisResult.cables_orphaned.length}</span>
                </div>
              </div>

              {/* Repatching details lists */}
              <div className="border border-slate-800 rounded-lg overflow-hidden">
                <div className="bg-slate-950/40 px-3 py-2 border-b border-slate-800 font-bold text-slate-300">
                  План перекоммутации кабелей:
                </div>
                <div className="p-3 space-y-3 max-h-[220px] overflow-y-auto custom-scrollbar text-[11px]">
                  {analysisResult.cables_to_unpatch.length === 0 ? (
                    <div className="text-slate-500 text-center py-2">Кабельные соединения отсутствуют</div>
                  ) : (
                    <div className="space-y-2">
                      {/* Repatch items */}
                      {analysisResult.cables_to_repatch.map((c: any) => (
                        <div key={c.cable_id} className="flex items-center gap-2 text-slate-300">
                          <ChevronRight className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                          <span>Кабель <strong className="text-cyan-400">{c.cable_id.slice(0, 8)}</strong>: порт <strong className="text-slate-100">{c.local_port}</strong> переподключить на <strong className="text-slate-100">{c.new_local_port}</strong></span>
                        </div>
                      ))}
                      {/* Orphaned items */}
                      {analysisResult.cables_orphaned.map((c: any) => (
                        <div key={c.cable_id} className="flex items-center gap-2 text-amber-400">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                          <span>Кабель <strong className="text-amber-300">{c.cable_id.slice(0, 8)}</strong>: порт <strong className="text-slate-100">{c.local_port}</strong> отсутствует на новой модели (останется отключенным)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/20 flex justify-end gap-2.5">
          <button 
            onClick={onClose}
            className="px-4 py-2 hover:bg-slate-800 border border-slate-800 rounded-lg text-slate-300 transition text-xs font-mono font-medium"
          >
            Отмена
          </button>
          <button 
            onClick={handleGenerateWorkOrder}
            disabled={!selectedCatalogId || !!analysisError || analyzing || generating}
            className="flex items-center gap-1.5 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition text-xs font-mono font-medium disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-cyan-600/20"
          >
            {generating && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
            Сгенерировать наряд
          </button>
        </div>
      </PropertyPanel>
    );
};
